import React, { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { MessageCircle, Send, CheckCircle2, Clock, Settings, Zap, UserRound } from "lucide-react";
import api from "@/lib/api";

const labels = {
  assigned: "Asignada",
  active: "En conversación",
  completed: "Completada",
};

const Conversation = ({ item, reload, consultant = false }) => {
  const [text, setText] = useState("");
  const consultantHasReplied = (item.messages || []).some(
    (message) => message.sender_id === item.assigned_consultant_id && String(message.text || "").trim(),
  );
  const send = async () => {
    if (!text.trim()) return;
    try {
      await api.post("/api/talk-services/" + item.id + "/messages", { text });
      setText("");
      reload();
      toast.success("Mensaje enviado");
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo enviar");
    }
  };
  const complete = async () => {
    try {
      await api.post("/api/consultor/talk-services/" + item.id + "/complete");
      reload();
      toast.success("Servicio completado");
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo completar");
    }
  };
  return (
    <article className="kd9-card p-5 space-y-3">
      <div className="flex flex-wrap justify-between gap-3">
        <div>
          <div className="font-cinzel kd9-gold-text">{item.service_kind === "quick_consultation" ? "CONSULTA RÁPIDA" : "HABLAR CON UN CONSULTOR"}</div>
          <div className="font-mont text-xs">{labels[item.status] || item.status}</div>
        </div>
        {consultant ? (
          <div className="flex items-center gap-2">
            <UserRound size={22} />
            <div><div className="font-mont text-xs">{item.user_email || "Usuario KD9"}</div><div className="font-mont text-[10px]" style={{ color: "var(--kd9-text-soft)" }}>Usuario de la consulta</div></div>
          </div>
        ) : item.selected_consultant_name && (
          <div className="flex items-center gap-2">
            {item.selected_consultant_avatar_url ? <img src={item.selected_consultant_avatar_url} alt="" className="h-10 w-10 rounded-full object-cover" /> : <UserRound size={22} />}
            <div><div className="font-mont text-xs">{item.selected_consultant_name}</div><div className="font-mont text-[10px]" style={{ color: "var(--kd9-text-soft)" }}>{item.selected_consultant_specialty}</div></div>
          </div>
        )}
      </div>
      <p className="font-cormorant text-lg">{item.question}</p>
      {["assigned", "active", "completed"].includes(item.status) && (
        <div className="rounded-lg border p-3 space-y-2" style={{ borderColor: "var(--kd9-border)" }}>
          {(item.messages || []).map((message, index) => {
            const fromConsultant = message.sender_id === item.assigned_consultant_id;
            return <div key={index} className="font-mont text-xs"><span style={{ color: "var(--kd9-text-soft)" }}>{new Date(message.at).toLocaleString()} · {fromConsultant ? "Consultor" : "Usuario"} · </span>{message.text}</div>;
          })}
        </div>
      )}
      {["assigned", "active"].includes(item.status) && (
        <div className="flex flex-wrap gap-2">
          <input className="kd9-input min-w-0 flex-1" value={text} onChange={(e) => setText(e.target.value)} placeholder="Escribe un mensaje" />
          <button className="kd9-btn-gold px-4" onClick={send}><Send size={15} /></button>
          {consultant && <button className="kd9-btn-outline px-4 disabled:opacity-40" onClick={complete} disabled={item.service_kind === "quick_consultation" && !consultantHasReplied} title={item.service_kind === "quick_consultation" && !consultantHasReplied ? "Responde la consulta antes de completarla" : ""}><CheckCircle2 size={15} /> Completar</button>}
        </div>
      )}
      <div className="space-y-1">{(item.history || []).map((row, index) => <div key={index} className="font-mont text-[11px]" style={{ color: "var(--kd9-text-soft)" }}>{new Date(row.at).toLocaleString()} · {labels[row.action] || row.action}</div>)}</div>
    </article>
  );
};

const ServicePage = ({ kind }) => {
  const quick = kind === "quick_consultation";
  const configUrl = quick ? "/api/talk-services/quick/config" : "/api/talk-services/consultant/config";
  const createUrl = quick ? "/api/talk-services/quick" : "/api/talk-services/consultant";
  const [config, setConfig] = useState(null);
  const [consultants, setConsultants] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [question, setQuestion] = useState("");
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const busyRef = useRef(false);
  const serviceCost = Number(config?.energy_cost || 0);
  const consultantsAvailable = consultants.length > 0;
  const [idempotencyKey, setIdempotencyKey] = useState(() => crypto.randomUUID());

  const load = useCallback(async () => {
    const [mine, cfg, available] = await Promise.all([
      api.get("/api/talk-services/mine?kind=" + kind),
      api.get(configUrl),
      api.get("/api/talk-services/consultants?kind=" + kind),
    ]);
    setItems(mine.data);
    setConfig(cfg.data);
    setConsultants(available.data);
    setSelectedId((current) => available.data.some((consultant) => consultant.id === current) ? current : "");
  }, [configUrl, kind]);

  useEffect(() => {
    load().catch(() => toast.error("No se pudo cargar el servicio"));
    const interval = window.setInterval(() => load().catch(() => {}), 5000);
    const refresh = () => load().catch(() => {});
    window.addEventListener("focus", refresh);
    return () => {
      window.clearInterval(interval);
      window.removeEventListener("focus", refresh);
    };
  }, [load]);

  const confirm = async () => {
    if (!selectedId) return toast.error("Selecciona un consultor disponible");
    if (busy) return;
    if (busyRef.current) return;
    busyRef.current = true;
    setBusy(true);
    try {
      const payload = { question, preferred_consultant_id: selectedId };
      payload.idempotency_key = idempotencyKey;
      const { data } = await api.post(createUrl, payload);
      setQuestion("");
      setSelectedId("");
      setIdempotencyKey(crypto.randomUUID());
      toast.success(quick ? "Pago realizado. Tu Consulta Rápida ya está activa." : "Pago realizado. Tu solicitud para hablar con el consultor ya está activa.");
      await load();
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (detail?.code === "INSUFFICIENT_ENERGY") {
        toast.error("Fondos insuficientes. Recarga Energía KD9 para continuar.");
        window.setTimeout(() => window.location.assign(detail.redirect || "/app/energia-kd"), 900);
      } else {
        toast.error(typeof detail === "string" ? detail : detail?.message || "No se pudo crear la solicitud");
      }
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6">
      <header>
        <div className="font-cinzel kd9-gold-text text-2xl">{config?.title || (quick ? "CONSULTA RÁPIDA KD9" : "HABLAR CON UN CONSULTOR")}</div>
        <p className="font-cormorant text-lg mt-2" style={{ color: "var(--kd9-text-soft)" }}>{config?.description}</p>
        {config && (
          <div className="flex flex-wrap gap-3 mt-2 font-mont text-xs">
            <span><Clock size={13} className="inline mr-1" />{config.duration_min} minutos</span>
            <span className="font-cinzel kd9-gold-text">
              {serviceCost > 0
                ? `⚡ ${serviceCost.toLocaleString("es-CO")} Energía KD9`
                : "Costo pendiente de configuración"}
            </span>
          </div>
        )}
      </header>

      {config?.active ? (
        <section className="kd9-card p-5 space-y-5">
          <div>
            <h2 className="font-cinzel text-lg mb-1">CONSULTORES DISPONIBLES</h2>
            <p className="font-mont text-xs mb-4" style={{ color: "var(--kd9-text-soft)" }}>{quick ? "Selecciona quién atenderá tu consulta. El pago se realizará inmediatamente con tu wallet KD9." : "Selecciona el consultor. El pago se realizará inmediatamente con tu wallet KD9."}</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {consultants.map((consultant) => {
                const selected = consultant.id === selectedId;
                return (
                  <button
                    type="button"
                    key={consultant.id}
                    onClick={() => setSelectedId(consultant.id)}
                    disabled={consultant.energy_cost <= 0}
                    className="rounded-xl border p-4 text-left transition-all disabled:opacity-50"
                    style={{ borderColor: selected ? "var(--kd9-gold)" : "var(--kd9-border)", background: selected ? "rgba(212,175,55,.10)" : "var(--kd9-bg-2)" }}
                    aria-pressed={selected}
                  >
                    <div className="flex items-center gap-3">
                      {consultant.avatar_url ? <img src={consultant.avatar_url} alt="" className="h-16 w-16 rounded-full object-cover" /> : <div className="h-16 w-16 shrink-0 rounded-full grid place-items-center border" style={{ borderColor: "var(--kd9-border)" }}><UserRound size={25} /></div>}
                      <div className="min-w-0">
                        <div className="font-cinzel kd9-gold-text">{consultant.first_name}</div>
                        <div className="font-mont text-xs break-words">{consultant.specialty}</div>
                        <div className="font-mont text-[10px] mt-1" style={{ color: "#22C55E" }}>● Disponible</div>
                        <div className="font-cinzel mt-1">{consultant.energy_cost > 0 ? `⚡ ${Number(consultant.energy_cost).toLocaleString("es-CO")} Energía KD9` : "Costo por configurar"}</div>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
            {!consultantsAvailable && (
              <div
                className="rounded-xl border p-4 font-mont text-sm"
                style={{ borderColor: "rgba(245,158,11,.35)", color: "#FCD34D" }}
                data-testid="talk-service-unavailable"
              >
                No hay Consultores disponibles para este servicio en este momento.
                KD9 solo muestra profesionales activos, autorizados y con su perfil público listo.
                El servicio conserva su valor configurado y no realizará ningún cobro hasta que
                exista un profesional seleccionable.
              </div>
            )}
          </div>

          <div>
            <label className="font-mont text-xs uppercase tracking-wider">Describe lo que necesitas</label>
            <textarea className="kd9-input w-full min-h-28 mt-2" value={question} onChange={(e) => setQuestion(e.target.value)} placeholder="Cuéntanos qué necesitas" />
          </div>

          <div className="rounded-lg border p-4 font-mont text-xs space-y-1" style={{ borderColor: "var(--kd9-border)" }}>
            {quick ? <>
              <div>1. Confirmas el consultor y la Consulta Rápida.</div>
              <div>2. KD9 valida y debita la energía disponible.</div>
              <div>3. La consulta se activa inmediatamente y entra a la bolsa correspondiente.</div>
            </> : <>
              <div>1. Confirmas el consultor y el servicio.</div>
              <div>2. KD9 valida y debita la energía disponible.</div>
              <div>3. La solicitud se activa inmediatamente en la bolsa correspondiente.</div>
            </>}
          </div>

          <button
            className="kd9-btn-gold w-full sm:w-auto disabled:opacity-40 disabled:cursor-not-allowed"
            disabled={busy || question.trim().length < 5 || !selectedId || serviceCost <= 0 || !consultantsAvailable}
            onClick={confirm}
            data-testid="talk-service-cta"
          >
            {quick ? <Zap size={15} /> : <MessageCircle size={15} />}{" "}
            {busy
              ? "Procesando..."
              : !consultantsAvailable
                ? "Sin Consultores disponibles"
                : serviceCost <= 0
                  ? "Costo pendiente de configuración"
                  : quick
                    ? "Pagar con Energía KD9 y activar"
                    : "Pagar con Energía KD9 y hablar"}
          </button>
        </section>
      ) : <div className="kd9-card p-5">Este servicio está temporalmente desactivado por Dirección.</div>}

      <section className="space-y-3">
        <h2 className="font-cinzel text-lg">HISTORIAL DEL SERVICIO</h2>
        {items.length ? items.map((item) => <Conversation key={item.id} item={item} reload={load} />) : <div className="kd9-card p-5">Aún no tienes solicitudes.</div>}
      </section>
    </div>
  );
};

export const ConsultantTalk = () => <ServicePage kind="consultant_chat" />;
export const QuickConsultation = () => <ServicePage kind="quick_consultation" />;

export const ConsultantServiceWork = () => {
  const [items, setItems] = useState([]);
  const load = useCallback(() => api.get("/api/consultor/talk-services/assigned").then(({ data }) => setItems(data)), []);
  useEffect(() => {
    load().catch(() => toast.error("No se pudieron cargar las consultas"));
    const interval = window.setInterval(() => load().catch(() => {}), 5000);
    const refresh = () => load().catch(() => {});
    window.addEventListener("focus", refresh);
    return () => {
      window.clearInterval(interval);
      window.removeEventListener("focus", refresh);
    };
  }, [load]);
  return <div className="space-y-5"><h1 className="font-cinzel kd9-gold-text text-2xl">CONSULTAS DIRECTAS</h1><p className="font-mont text-xs" style={{ color: "var(--kd9-text-soft)" }}>Las conversaciones se actualizan automáticamente mientras permaneces en esta pantalla.</p>{items.length ? items.map((item) => <Conversation key={item.id} item={item} consultant reload={load} />) : <div className="kd9-card p-5">No tienes consultas asignadas.</div>}</div>;
};

const ConfigCard = ({ kind, form, setForm, save }) => (
  <section className="kd9-card p-6 grid gap-4">
    <h2 className="font-cinzel kd9-gold-text text-lg">{kind === "quick" ? "CONSULTA RÁPIDA" : "HABLAR CON UN CONSULTOR"}</h2>
    <label className="font-mont text-xs">Nombre<input className="kd9-input w-full mt-1" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></label>
    <label className="font-mont text-xs">Descripción<textarea className="kd9-input w-full mt-1 min-h-24" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></label>
    <div className="grid sm:grid-cols-2 gap-4"><label className="font-mont text-xs">Duración (min)<input type="number" className="kd9-input w-full mt-1" value={form.duration_min} onChange={(e) => setForm({ ...form, duration_min: e.target.value })} /></label><label className="font-mont text-xs">Costo en Energía KD9<input type="number" min="0" step="1" className="kd9-input w-full mt-1" value={form.energy_cost} onChange={(e) => setForm({ ...form, energy_cost: e.target.value })} /></label></div>
    <label className="font-mont text-xs flex gap-2 items-center"><input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} /> Servicio activo</label>
    <button className="kd9-btn-gold w-fit" onClick={save}><Settings size={15} /> Guardar configuración</button>
  </section>
);

export const QuickServiceDirector = () => {
  const [quick, setQuick] = useState(null);
  const [consultant, setConsultant] = useState(null);
  useEffect(() => {
    Promise.all([api.get("/api/talk-services/quick/config"), api.get("/api/talk-services/consultant/config")])
      .then(([q, c]) => { setQuick(q.data); setConsultant(c.data); });
  }, []);
  const save = async (kind, form, setter) => {
    try {
      const path = kind === "quick" ? "/api/director/talk-services/quick/config" : "/api/director/talk-services/consultant/config";
      const { data } = await api.put(path, { ...form, duration_min: Number(form.duration_min), energy_cost: Number(form.energy_cost) });
      setter(data);
      toast.success("Configuración guardada con auditoría");
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo guardar");
    }
  };
  if (!quick || !consultant) return <div>Cargando…</div>;
  return <div className="space-y-5 max-w-3xl"><header><h1 className="font-cinzel kd9-gold-text text-2xl">SERVICIOS DE CONSULTOR</h1><p className="font-cormorant text-lg">Información, duración y valor real de cada flujo.</p></header>
    <ConfigCard kind="quick" form={quick} setForm={setQuick} save={() => save("quick", quick, setQuick)} />
    <ConfigCard kind="consultant" form={consultant} setForm={setConsultant} save={() => save("consultant", consultant, setConsultant)} />
  </div>;
};
